import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, MapPin, ArrowRight, Package } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCities } from "@/context/CityContext";
import { Logo } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CitySelection() {
  const nav = useNavigate();
  const { cities: saved, setCities, visitor, visitorType } = useCities();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(saved || []);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");

  useEffect(() => {
    if (!visitor) { nav("/welcome", { replace: true }); return; }
    api.get("/cities?active=true&with_listings=true").then(({ data }) => { setList(data); setLoading(false); });
  }, [visitor, nav]);

  const toggle = (name) => {
    setSelected((cur) => {
      if (cur.includes(name)) return cur.filter((c) => c !== name);
      if (cur.length >= 2) { toast.error("You can select a maximum of 2 cities"); return cur; }
      return [...cur, name];
    });
  };

  const cont = () => {
    if (!selected.length) { toast.error("Select at least 1 city"); return; }
    api.post("/visitors", { mobile: visitor, type: visitorType || undefined, cities: selected }).catch(() => {});
    setCities(selected);
    nav("/marketplace");
  };

  const states = [...new Set(list.map((c) => c.state).filter(Boolean))].sort();
  const shown = stateFilter ? list.filter((c) => c.state === stateFilter) : list;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center mb-10"><Logo onClick={() => {}} /></div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-4">
            <MapPin className="h-3.5 w-3.5" /> Step 2 of 2
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Select Your City
          </h1>
          <p className="text-muted-foreground mt-3 text-base font-medium max-w-lg mx-auto">
            Pick up to <span className="text-foreground font-bold">2 cities</span>. We only show cities that currently have <span className="text-foreground font-bold">live tile stock</span> listed.
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-8 grid sm:grid-cols-2 gap-3">
          <Select value={stateFilter || "all"} onValueChange={(v) => setStateFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="state-select" className="h-12"><SelectValue placeholder="Select State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value="" onValueChange={(v) => toggle(v)}>
            <SelectTrigger data-testid="district-select" className="h-12"><SelectValue placeholder="Select District / City" /></SelectTrigger>
            <SelectContent>
              {shown.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}{c.state ? ` · ${c.state}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-20">Loading cities…</div>
        ) : shown.length === 0 ? (
          <div data-testid="no-listed-cities" className="text-center py-20 max-w-md mx-auto">
            <div className="inline-grid place-items-center h-16 w-16 rounded-2xl bg-primary/10 text-primary mb-4"><MapPin className="h-8 w-8" /></div>
            <h3 className="font-heading text-xl font-extrabold">No live stock yet{stateFilter ? ` in ${stateFilter}` : ""}</h3>
            <p className="text-muted-foreground text-sm font-medium mt-2">Sellers are still uploading their clearance stock. Cities appear here the moment listings go live. Please check back shortly{stateFilter ? " or pick another state" : ""}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((c) => {
              const active = selected.includes(c.name);
              return (
                <button
                  key={c.id}
                  data-testid={`city-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => toggle(c.name)}
                  className={`relative group overflow-hidden rounded-2xl border-4 aspect-[4/3] transition-all duration-300 text-left shadow-lg ${
                    active ? "border-primary ring-4 ring-primary/25 scale-[0.98]" : "border-white/70 hover:border-primary/60 hover:-translate-y-1"
                  }`}
                >
                  <img src={c.image} alt={c.name}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&q=60"; }}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />

                  <div data-testid={`city-listing-count-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-primary text-white text-xs font-extrabold px-3 py-1.5 shadow-lg">
                    <Package className="h-3.5 w-3.5" /> {c.listing_count} live {c.listing_count === 1 ? "listing" : "listings"}
                  </div>

                  {active && (
                    <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-primary grid place-items-center shadow-lg animate-in zoom-in">
                      <Check className="h-5 w-5 text-white" strokeWidth={3} />
                    </div>
                  )}
                  <div className="absolute bottom-0 p-5 text-white">
                    <div className="font-heading font-extrabold text-2xl sm:text-3xl leading-tight tracking-tight drop-shadow">{c.name}</div>
                    <div className="text-sm text-white/70 font-semibold">{c.state}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="sticky bottom-4 mt-8 flex justify-center">
          <Button
            data-testid="confirm-city-btn"
            onClick={cont}
            disabled={!selected.length}
            className="rounded-full h-14 px-10 text-base font-bold shadow-2xl gap-2"
          >
            Continue {selected.length ? `(${selected.length})` : ""} <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
